import React, {FC, useState, useEffect, useCallback} from 'react'
import {useNavigate} from 'react-router-dom'
import {KTSVG} from '../../../../../_metronic/helpers'
import {PmsJobRecord} from '../../core/pms/_models'
import {
  getJobDueReasonBadgeColor,
  getJobDueReasonLabel,
  getJobDueReasonMetaText,
  formatJobDate,
  getDisplayedJobState,
  getDisplayedJobStateBadgeColor,
  isMaskedJob,
} from './pmsJobHelpers'
import {
  listJobAttachments,
  listWorkOrdersByPlanLine,
  reassignPmsJob,
} from '../../core/pms/_requests'
import {PmsJobAttachmentDto} from '../../core/pms/_models'
import {JobAttachmentsModal} from './JobAttachmentsModal'
import {WorkOrderDetailsModal} from './WorkOrderDetailsModal'
import {PlanLineLastDoneModal} from './PlanLineLastDoneModal'
import {getPartsBySubcomponent, getSubcomponentsByEquipment} from '../../core/_requests'
import {PartDto} from '../../core/_models'
import {toast} from 'react-toastify'
import SearchableSelect from '../../../../shared/components/SearchableSelect'

interface Props {
  visible: boolean
  onClose: () => void
  record: PmsJobRecord
  onRefresh: () => void
  hideDefectViewButton?: boolean
  onViewDefect?: (defectId: number) => void
}

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(15, 23, 42, 0.45)',
  position: 'fixed',
  inset: 0,
  zIndex: 2000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backdropFilter: 'blur(2px)',
  padding: '24px',
  overflow: 'auto',
  overscrollBehavior: 'contain',
}

const niceEnum = (s?: string | null) =>
  s
    ? s
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
    : '-'

const FlagIcon = ({className}: {className?: string}) => (
  <svg
    width='14'
    height='14'
    viewBox='0 0 24 24'
    fill='currentColor'
    aria-hidden='true'
    className={className}
  >
    <path d='M5 2h2v20H5z' />
    <path d='M7 3h12l-3 4 3 4H7z' />
  </svg>
)

export const PmsJobDetailModal: FC<Props> = ({
  visible,
  onClose,
  record,
  onRefresh,
  hideDefectViewButton,
  onViewDefect,
}) => {
  const navigate = useNavigate()
  const [attachments, setAttachments] = useState<PmsJobAttachmentDto[]>([])
  const [parts, setParts] = useState<PartDto[]>([])
  const [subcomponentsWithParts, setSubcomponentsWithParts] = useState<
    Array<{subcomponent: any; parts: PartDto[]}>
  >([])
  const [expandedSubcomponents, setExpandedSubcomponents] = useState<Set<number>>(new Set())
  const [loadingAttachments, setLoadingAttachments] = useState(false)
  const [loadingParts, setLoadingParts] = useState(false)
  const [attachmentsModalOpen, setAttachmentsModalOpen] = useState(false)
  const [newAssigneeUserId, setNewAssigneeUserId] = useState<string>('')
  const [reassigning, setReassigning] = useState(false)
  const [partsExpanded, setPartsExpanded] = useState(false)
  const [partsPage, setPartsPage] = useState(1)
  const [partsPageSize, setPartsPageSize] = useState(10)
  const [workOrders, setWorkOrders] = useState<PmsJobRecord[]>([])
  const [loadingWorkOrders, setLoadingWorkOrders] = useState(false)
  const [workOrderView, setWorkOrderView] = useState<PmsJobRecord | null>(null)
  const [workOrderViewOpen, setWorkOrderViewOpen] = useState(false)
  const [workOrdersPage, setWorkOrdersPage] = useState(1)
  const [workOrdersPageSize, setWorkOrdersPageSize] = useState(5)
  const [lastDoneModalOpen, setLastDoneModalOpen] = useState(false)

  const handleViewDefect = () => {
    if (record.defectId) {
      if (onViewDefect) {
        onViewDefect(record.defectId)
      } else {
        navigate(`/qhse/defects-list`, {state: {defectId: record.defectId}})
      }
      if (!onViewDefect) {
        onClose()
      }
    }
  }

  const handleReassign = async () => {
    if (isMaskedJob(record)) {
      toast.error('For this job to update, please contact your admin', {position: 'top-center'})
      return
    }
    if (!record.id || !newAssigneeUserId?.trim()) {
      toast.error('Please enter crew/team name', {position: 'top-center'})
      return
    }
    setReassigning(true)
    try {
      const trimmed = newAssigneeUserId.trim()
      await reassignPmsJob(record.id, trimmed, undefined)
      toast.success('Assigned to updated successfully', {position: 'top-center'})
      setNewAssigneeUserId(trimmed)
      onRefresh()
    } catch (error: any) {
      console.error('Error reassigning job:', error)
      toast.error('Failed to reassign job', {position: 'top-center'})
    } finally {
      setReassigning(false)
    }
  }

  const loadAttachments = useCallback(async () => {
    if (!record.id) return
    setLoadingAttachments(true)
    try {
      const data = await listJobAttachments(record.id)
      setAttachments(data)
    } catch (error) {
      console.error('Error loading attachments:', error)
    } finally {
      setLoadingAttachments(false)
    }
  }, [record.id])

  const loadWorkOrders = useCallback(async () => {
    if (!record.planLineId) {
      setWorkOrders([])
      return
    }
    setLoadingWorkOrders(true)
    try {
      const data = await listWorkOrdersByPlanLine(record.planLineId)
      const filtered = data.filter((wo) => wo.id !== record.id)
      const getSortTime = (wo: PmsJobRecord) => {
        const raw = wo.actualCompletionDate || wo.updatedAt || wo.createdAt || ''
        const dt = raw ? new Date(raw).getTime() : 0
        return Number.isNaN(dt) ? 0 : dt
      }
      const sorted = [...filtered].sort((a, b) => getSortTime(b) - getSortTime(a))
      setWorkOrders(sorted)
      setWorkOrdersPage(1)
    } catch (error) {
      console.error('Error loading work orders:', error)
      toast.error('Failed to load work orders', {position: 'top-center'})
      setWorkOrders([])
    } finally {
      setLoadingWorkOrders(false)
    }
  }, [record.planLineId, record.id])

  const handleLastDoneSaved = () => {
    loadWorkOrders()
    onRefresh()
  }

  const loadParts = useCallback(async () => {
    setLoadingParts(true)
    try {
      if (record.subComponentId) {
        // If job is for subcomponent, fetch parts for that subcomponent
        const data = await getPartsBySubcomponent(record.subComponentId)
        setParts(data)
        setSubcomponentsWithParts([])
      } else if (record.equipmentId) {
        // If job is for equipment, fetch all subcomponents and their parts in tree structure
        const subcomponents = await getSubcomponentsByEquipment(record.equipmentId)
        const subcomponentsWithPartsData: Array<{subcomponent: any; parts: PartDto[]}> = []
        for (const sub of subcomponents) {
          if (sub.id) {
            const subParts = await getPartsBySubcomponent(sub.id)
            subcomponentsWithPartsData.push({
              subcomponent: sub,
              parts: subParts,
            })
          }
        }
        setSubcomponentsWithParts(subcomponentsWithPartsData)
        // Also set flat parts list for backward compatibility
        const allParts: PartDto[] = subcomponentsWithPartsData.flatMap((item) => item.parts)
        setParts(allParts)
        // Expand all subcomponents by default
        setExpandedSubcomponents(
          new Set(
            subcomponentsWithPartsData
              .map((item) => item.subcomponent.id)
              .filter((id): id is number => id !== undefined)
          )
        )
      } else {
        setParts([])
        setSubcomponentsWithParts([])
      }
    } catch (error) {
      console.error('Error loading parts:', error)
      setParts([])
      setSubcomponentsWithParts([])
    } finally {
      setLoadingParts(false)
    }
  }, [record.subComponentId, record.equipmentId])

  const toggleSubcomponent = (subcomponentId: number) => {
    setExpandedSubcomponents((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(subcomponentId)) {
        newSet.delete(subcomponentId)
      } else {
        newSet.add(subcomponentId)
      }
      return newSet
    })
  }

  useEffect(() => {
    if (visible && record.id) {
      loadAttachments()
      loadParts()
      loadWorkOrders()
    }
  }, [visible, record.id, loadAttachments, loadParts, loadWorkOrders])

  useEffect(() => {
    if (visible) {
      setPartsExpanded(false)
      setPartsPage(1)
    }
  }, [visible, record.id, record.subComponentId])

  useEffect(() => {
    if (visible) {
      setNewAssigneeUserId(record.crewUndertaking || '')
    }
  }, [visible, record.crewUndertaking])

  if (!visible) return null

  const totalWorkOrders = workOrders.length
  const workOrdersTotalPages = Math.max(1, Math.ceil(totalWorkOrders / workOrdersPageSize))
  const workOrdersPageSafe = Math.min(workOrdersPage, workOrdersTotalPages)
  const workOrdersStart = (workOrdersPageSafe - 1) * workOrdersPageSize
  const workOrdersPageItems = workOrders.slice(
    workOrdersStart,
    workOrdersStart + workOrdersPageSize
  )

  const totalParts = parts.length
  const partsTotalPages = Math.max(1, Math.ceil(totalParts / partsPageSize))
  const partsPageSafe = Math.min(partsPage, partsTotalPages)
  const partsStart = (partsPageSafe - 1) * partsPageSize
  const partsPageItems = parts.slice(partsStart, partsStart + partsPageSize)

  return (
    <>
      <div
        className='d-flex align-items-center justify-content-center'
        style={WRAP_STYLE}
        tabIndex={-1}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div
          role='dialog'
          aria-modal='true'
          style={{
            width: 'min(1400px, 96vw)',
            height: 'min(900px, 92vh)',
            background: '#ffffff',
            borderRadius: '18px',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.22)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              padding: '20px 24px',
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '16px',
              flexShrink: 0,
            }}
          >
            <div>
              <h5
                className='mb-1'
                style={{
                  margin: 0,
                  color: '#111827',
                  fontSize: '1.2rem',
                  fontWeight: 700,
                }}
              >
                Job Details
              </h5>
              <div style={{color: '#6B7280', fontSize: '0.92rem'}}>
                Review the job, attachments, parts, and previous work orders.
              </div>
            </div>
            <button
              type='button'
              onClick={onClose}
              className='btn btn-sm'
              style={{
                border: '1px solid #D1D5DB',
                background: '#fff',
                color: '#374151',
                width: '38px',
                height: '38px',
                borderRadius: '10px',
                fontSize: '18px',
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>

          <div
            className='flex-grow-1 overflow-auto px-4 py-3'
            style={{maxHeight: 'calc(92vh - 140px)'}}
          >
            {/* Job For Section - Prominent Display */}
            <div className='card bg-light-primary mb-4'>
              <div className='card-body'>
                <h6 className='fw-bold mb-3'>Job For</h6>
                <div className='d-flex flex-wrap align-items-center gap-2 mb-2'>
                  {record.equipmentName && (
                    <span className='badge badge-light-primary fs-6'>
                      <KTSVG
                        path='/media/icons/duotune/general/gen025.svg'
                        className='svg-icon-4 me-1'
                      />
                      Equipment: {record.equipmentName}
                    </span>
                  )}
                  {record.subComponentName && (
                    <span className='badge badge-light-success fs-6'>
                      <KTSVG
                        path='/media/icons/duotune/general/gen023.svg'
                        className='svg-icon-4 me-1'
                      />
                      SubComponent: {record.subComponentName}
                    </span>
                  )}
                </div>
                <div className='text-muted fs-7'>
                  This job is for:{' '}
                  <strong>{record.subComponentName || record.equipmentName || 'N/A'}</strong>
                </div>
              </div>
            </div>

            {/* Job Info */}
            <div className='row g-3 mb-4'>
              <div className='col-md-6'>
                <div className='fw-semibold text-muted fs-7 mb-1'>Job Code</div>
                <div className='fw-bold'>{record.jobCode || '-'}</div>
              </div>
              <div className='col-md-6'>
                <div className='fw-semibold text-muted fs-7 mb-1'>State</div>
                <div>
                  <span className={`badge ${getDisplayedJobStateBadgeColor(record)}`}>
                    {niceEnum(getDisplayedJobState(record))}
                  </span>
                </div>
              </div>
              <div className='col-md-6'>
                <div className='fw-semibold text-muted fs-7 mb-1'>Job Type</div>
                <div>
                  {record.jobType ? (
                    <span className='badge badge-light-info'>{record.jobType}</span>
                  ) : (
                    '-'
                  )}
                </div>
              </div>
              <div className='col-md-6'>
                <div className='fw-semibold text-muted fs-7 mb-1'>Vessel</div>
                <div className='fw-bold'>{record.vesselName || '-'}</div>
              </div>
              <div className='col-12'>
                <div className='fw-semibold text-muted fs-7 mb-1'>Job Description</div>
                <div className='fw-bold d-flex align-items-center gap-2 flex-wrap'>
                  <span>{record.jobDescription || record.title || '-'}</span>
                  {record.flagged && (
                    <span className='badge bg-danger text-white d-inline-flex align-items-center gap-1'>
                      <FlagIcon className='text-white' />
                      Flagged
                    </span>
                  )}
                </div>
              </div>
              <div className='col-12'>
                <div className='fw-semibold text-muted fs-7 mb-1'>Job Instructions</div>
                <div className='fw-bold'>{record.jobInstructions || '-'}</div>
              </div>
              <div className='col-md-6'>
                <div className='fw-semibold text-muted fs-7 mb-1'>Job Due Reason</div>
                <div className='d-flex flex-column gap-1'>
                  <span className={`badge ${getJobDueReasonBadgeColor(record)}`}>
                    {getJobDueReasonLabel(record)}
                  </span>
                  <small className='text-muted'>{getJobDueReasonMetaText(record)}</small>
                </div>
              </div>
              {!hideDefectViewButton && record.defectNumber && record.defectId && (
                <div className='col-md-12'>
                  <div className='fw-semibold text-muted fs-7 mb-1'>Linked Defect</div>
                  <div className='d-flex align-items-center gap-2 flex-wrap'>
                    <span
                      className='badge badge-light-primary cursor-pointer'
                      onClick={handleViewDefect}
                      style={{cursor: 'pointer'}}
                      title='Click to view defect'
                    >
                      {record.defectNumber}
                    </span>
                    <button
                      className='btn btn-sm btn-light-primary'
                      onClick={handleViewDefect}
                      title='View Defect'
                    >
                      <KTSVG
                        path='/media/icons/duotune/general/gen057.svg'
                        className='svg-icon-3'
                      />
                      View Defect
                    </button>
                    <span className='text-muted fs-7 ms-2'>
                      (Job created from defect job request)
                    </span>
                  </div>
                </div>
              )}
              <div className='col-md-6'>
                <div className='fw-semibold text-muted fs-7 mb-1'>Assigned To</div>
                <div className='d-flex align-items-center gap-2'>
                  <input
                    type='text'
                    className='form-control form-control-sm'
                    placeholder='Crew/Team Name'
                    value={newAssigneeUserId}
                    onChange={(e) => setNewAssigneeUserId(e.target.value)}
                    disabled={isMaskedJob(record)}
                    style={{maxWidth: '240px'}}
                  />
                  <button
                    className='btn btn-sm btn-primary'
                    onClick={handleReassign}
                    disabled={reassigning || !newAssigneeUserId?.trim() || isMaskedJob(record)}
                  >
                    {reassigning ? 'Saving...' : 'Save'}
                  </button>
                </div>
                {isMaskedJob(record) && (
                  <div className='text-danger fs-7 mt-1'>
                    For this job to update, please contact your admin.
                  </div>
                )}
              </div>
              {record.actualCounterAtCompletion && (
                <div className='col-md-6'>
                  <div className='fw-semibold text-muted fs-7 mb-1'>Counter at Completion</div>
                  <div>{record.actualCounterAtCompletion} hrs</div>
                </div>
              )}
              {record.remarks && (
                <div className='col-12'>
                  <div className='fw-semibold text-muted fs-7 mb-1'>Remarks</div>
                  <div className='border rounded p-2 bg-light-subtle' style={{minHeight: '60px'}}>
                    {record.remarks}
                  </div>
                </div>
              )}
              {record.findings && (
                <div className='col-12'>
                  <div className='fw-semibold text-muted fs-7 mb-1'>Findings</div>
                  <div className='border rounded p-2 bg-light-subtle' style={{minHeight: '60px'}}>
                    {record.findings}
                  </div>
                </div>
              )}
              {record.checklist && (
                <div className='col-12'>
                  <div className='fw-semibold text-muted fs-7 mb-2'>Checklist</div>
                  <div className='card bg-light p-3'>
                    <div className='form-check-list'>
                      {record.checklist
                        .split('\n')
                        .filter((line) => line.trim().length > 0)
                        .map((line, idx) => (
                          <div key={idx} className='form-check mb-2'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              id={`checklist-${idx}`}
                              disabled
                              readOnly
                            />
                            <label className='form-check-label' htmlFor={`checklist-${idx}`}>
                              {line.trim()}
                            </label>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              )}
              {record.procedureFileRef && (
                <div className='col-12'>
                  <div className='fw-semibold text-muted fs-7 mb-1'>OEM Manual Reference</div>
                  <div>
                    <a
                      href={record.procedureFileRef}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-primary'
                    >
                      {record.procedureFileRef}
                    </a>
                  </div>
                </div>
              )}
              {record.safetyPrecautions && (
                <div className='col-12'>
                  <div className='fw-semibold text-muted fs-7 mb-1'>Safety Precautions</div>
                  <div className='alert alert-warning mb-0'>{record.safetyPrecautions}</div>
                </div>
              )}
              {record.riskPermitRequired && (
                <div className='col-12'>
                  <div className='fw-semibold text-muted fs-7 mb-1'>Permit Required</div>
                  <div>
                    <span className='badge badge-light-warning'>Yes</span>
                    {record.permitType && (
                      <span className='badge badge-light-info ms-2'>{record.permitType}</span>
                    )}
                  </div>
                </div>
              )}
              {record.measuredValues && (
                <div className='col-12'>
                  <div className='fw-semibold text-muted fs-7 mb-1'>Measured Values</div>
                  <div className='border rounded p-2 bg-light-subtle' style={{minHeight: '60px'}}>
                    {record.measuredValues}
                  </div>
                </div>
              )}
              {record.riskAssessmentNumber && (
                <div className='col-md-6'>
                  <div className='fw-semibold text-muted fs-7 mb-1'>Risk Assessment Number</div>
                  <div>{record.riskAssessmentNumber}</div>
                </div>
              )}
              {record.flagged && (
                <div className='col-12'>
                  <div className='fw-semibold text-muted fs-7 mb-1'>Flagged Job</div>
                  <div className='alert alert-danger mb-0 d-flex align-items-center gap-2'>
                    <FlagIcon className='text-danger' />
                    <span>This work order was flagged at completion.</span>
                  </div>
                </div>
              )}
              {record.flaggedComment && (
                <div className='col-12'>
                  <div className='fw-semibold text-muted fs-7 mb-1'>Flagged Comment</div>
                  <div className='border rounded p-2 bg-light-subtle' style={{minHeight: '60px'}}>
                    {record.flaggedComment}
                  </div>
                </div>
              )}
            </div>

            {/* Parts Under Equipment/SubComponent */}
            {(record.equipmentId || record.subComponentId) && (
              <div className='mb-5'>
                <div className='d-flex justify-content-between align-items-center mb-3 p-3 rounded bg-light-subtle border'>
                  <div>
                    <h6 className='fw-bold m-0'>
                      {record.subComponentName
                        ? 'Parts allocated to this SubComponent'
                        : 'Parts allocated to this Equipment'}
                    </h6>
                    <div className='text-muted fs-7'>
                      Inventory details linked to the selected hierarchy
                    </div>
                  </div>
                  {record.subComponentId && (
                    <button
                      type='button'
                      className='btn btn-sm btn-light'
                      onClick={() => setPartsExpanded((prev) => !prev)}
                    >
                      {partsExpanded ? 'Collapse' : 'Expand'}
                    </button>
                  )}
                </div>
                {loadingParts ? (
                  <div className='text-center py-3'>
                    <div className='spinner-border spinner-border-sm' role='status'>
                      <span className='visually-hidden'>Loading...</span>
                    </div>
                  </div>
                ) : record.equipmentId &&
                  !record.subComponentId &&
                  subcomponentsWithParts.length > 0 ? (
                  // Tree structure for equipment jobs
                  <div className='card'>
                    <div className='card-body p-0'>
                      {subcomponentsWithParts.map((item) => {
                        const isExpanded =
                          item.subcomponent.id && expandedSubcomponents.has(item.subcomponent.id)
                        return (
                          <div key={item.subcomponent.id} className='border-bottom'>
                            <div
                              className='d-flex align-items-center justify-content-between p-3 cursor-pointer'
                              style={{
                                cursor: 'pointer',
                                backgroundColor: isExpanded ? '#f8f9fa' : 'white',
                              }}
                              onClick={() =>
                                item.subcomponent.id && toggleSubcomponent(item.subcomponent.id)
                              }
                            >
                              <div className='d-flex align-items-center gap-2'>
                                <KTSVG
                                  path={
                                    isExpanded
                                      ? '/media/icons/duotune/arrows/arr074.svg'
                                      : '/media/icons/duotune/arrows/arr071.svg'
                                  }
                                  className='svg-icon-3'
                                />
                                <div>
                                  <div className='fw-bold'>
                                    {item.subcomponent.name || 'Unnamed SubComponent'}
                                  </div>
                                  {item.subcomponent.subComponentCode && (
                                    <div className='text-muted fs-7'>
                                      ({item.subcomponent.subComponentCode})
                                    </div>
                                  )}
                                </div>
                              </div>
                              <span className='badge badge-light-info'>
                                {item.parts.length} {item.parts.length === 1 ? 'part' : 'parts'}
                              </span>
                            </div>
                            {isExpanded && item.parts.length > 0 && (
                              <div className='px-3 pb-3'>
                                <div className='table-responsive'>
                                  <table className='table table-bordered table-sm align-middle table-hover mb-0'>
                                    <thead className='table-light text-muted text-uppercase fs-8'>
                                      <tr>
                                        <th>Part Name</th>
                                        <th>Part Code</th>
                                        <th>Item Long Description</th>
                                        <th>Draw No</th>
                                        <th>Drawing Position No</th>
                                        <th>Location</th>
                                        <th>ROB Qty</th>
                                        <th>Unit</th>
                                        <th>Min Threshold</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {item.parts.map((part) => (
                                        <tr key={part.id}>
                                          <td>{part.name || '-'}</td>
                                          <td>{part.code || '-'}</td>
                                          <td>{part.itemLongDescription || '-'}</td>
                                          <td>{part.drawNo || '-'}</td>
                                          <td>{part.drawingPositionNo || '-'}</td>
                                          <td>{part.location || '-'}</td>
                                          <td>{part.robQuantity ?? '-'}</td>
                                          <td>{part.unitOfMeasure || part.uom?.code || '-'}</td>
                                          <td>{part.minThreshold ?? '-'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                            {isExpanded && item.parts.length === 0 && (
                              <div className='px-3 pb-3 text-muted fs-7'>
                                No parts found for this subcomponent.
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ) : parts.length === 0 ? (
                  <div className='text-muted text-center py-3'>
                    No parts found for this {record.subComponentName ? 'subcomponent' : 'equipment'}
                    .
                  </div>
                ) : record.subComponentId ? (
                  <>
                    {!partsExpanded ? (
                      <div className='text-muted text-center py-3'>
                        <small>{parts.length} part(s) available. Click Expand to view.</small>
                      </div>
                    ) : (
                      <div className='card'>
                        <div className='card-body p-0'>
                          <div className='table-responsive'>
                            <table className='table table-bordered table-sm align-middle table-hover mb-0'>
                              <thead className='table-light text-muted text-uppercase fs-8'>
                                <tr>
                                  <th>Part Name</th>
                                  <th>Part Code</th>
                                  <th>Item Long Description</th>
                                  <th>Draw No</th>
                                  <th>Drawing Position No</th>
                                  <th>Location</th>
                                  <th>ROB Qty</th>
                                  <th>Unit</th>
                                  <th>Min Threshold</th>
                                </tr>
                              </thead>
                              <tbody>
                                {partsPageItems.map((part) => (
                                  <tr key={part.id}>
                                    <td>{part.name || '-'}</td>
                                    <td>{part.code || '-'}</td>
                                    <td>{part.itemLongDescription || '-'}</td>
                                    <td>{part.drawNo || '-'}</td>
                                    <td>{part.drawingPositionNo || '-'}</td>
                                    <td>{part.location || '-'}</td>
                                    <td>{part.robQuantity ?? '-'}</td>
                                    <td>{part.unitOfMeasure || part.uom?.code || '-'}</td>
                                    <td>{part.minThreshold ?? '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {parts.length > 0 && (
                            <div className='d-flex align-items-center justify-content-between px-3 py-2 border-top'>
                              <div className='text-muted fs-8'>
                                Showing {partsStart + 1}-
                                {Math.min(partsStart + partsPageSize, totalParts)} of {totalParts}
                              </div>
                              <div className='d-flex align-items-center gap-2'>
                                <SearchableSelect
                                  className='form-select form-select-sm text-dark'
                                  style={{width: '90px'}}
                                  value={partsPageSize}
                                  onChange={(e) => {
                                    setPartsPageSize(Number(e.target.value))
                                    setPartsPage(1)
                                  }}
                                >
                                  <option value={10}>10</option>
                                  <option value={20}>20</option>
                                  <option value={50}>50</option>
                                </SearchableSelect>
                                <button
                                  type='button'
                                  className='btn btn-sm btn-light'
                                  disabled={partsPageSafe <= 1}
                                  onClick={() => setPartsPage((prev) => Math.max(1, prev - 1))}
                                >
                                  Prev
                                </button>
                                <div className='text-muted fs-8'>
                                  Page {partsPageSafe} of {partsTotalPages}
                                </div>
                                <button
                                  type='button'
                                  className='btn btn-sm btn-light'
                                  disabled={partsPageSafe >= partsTotalPages}
                                  onClick={() =>
                                    setPartsPage((prev) => Math.min(partsTotalPages, prev + 1))
                                  }
                                >
                                  Next
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  // Flat table for equipment jobs without subcomponents list
                  <div className='table-responsive'>
                    <table className='table table-bordered table-sm align-middle table-hover'>
                      <thead className='table-light text-muted text-uppercase fs-8'>
                        <tr>
                          <th>Part Name</th>
                          <th>Part Code</th>
                          <th>Item Long Description</th>
                          <th>Draw No</th>
                          <th>Drawing Position No</th>
                          <th>Location</th>
                          <th>ROB Qty</th>
                          <th>Unit</th>
                          <th>Min Threshold</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parts.map((part) => (
                          <tr key={part.id}>
                            <td>{part.name || '-'}</td>
                            <td>{part.code || '-'}</td>
                            <td>{part.itemLongDescription || '-'}</td>
                            <td>{part.drawNo || '-'}</td>
                            <td>{part.drawingPositionNo || '-'}</td>
                            <td>{part.location || '-'}</td>
                            <td>{part.robQuantity ?? '-'}</td>
                            <td>{part.unitOfMeasure || part.uom?.code || '-'}</td>
                            <td>{part.minThreshold ?? '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Previous Work Orders */}
            <div className='mb-5'>
              <h6 className='fw-bold mb-3'>Previous Work Orders</h6>
              <div className='card bg-light'>
                <div className='card-body p-0'>
                  {loadingWorkOrders ? (
                    <div className='text-center py-4'>
                      <span className='spinner-border spinner-border-sm'></span> Loading history...
                    </div>
                  ) : workOrders.length === 0 ? (
                    <div className='text-muted text-center py-4'>
                      <small>No previous work orders found.</small>
                      {record.planLineId && (
                        <div className='mt-3'>
                          <button
                            type='button'
                            className='btn btn-sm btn-primary'
                            onClick={() => setLastDoneModalOpen(true)}
                          >
                            Add Last Done
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      className='table-responsive'
                      style={{maxHeight: '300px', overflowY: 'auto'}}
                    >
                      <table className='table table-sm table-bordered align-middle mb-0'>
                        <thead className='table-light sticky-top'>
                          <tr>
                            <th style={{width: '18%'}}>Completed Date</th>
                            <th style={{width: '15%'}}>Job Code</th>
                            <th style={{width: '24%'}}>Findings</th>
                            <th style={{width: '15%'}}>Flagged</th>
                            <th style={{width: '18%'}}>Flagged Comment</th>
                            <th style={{width: '10%'}}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {workOrdersPageItems.map((wo) => (
                            <tr key={wo.id} className={wo.flagged ? 'table-warning' : ''}>
                              <td>
                                {formatJobDate(
                                  wo.actualCompletionDate || wo.updatedAt || wo.createdAt
                                )}
                              </td>
                              <td>{wo.jobCode || '-'}</td>
                              <td>
                                <div
                                  className='text-truncate'
                                  style={{maxWidth: '260px'}}
                                  title={wo.findings || ''}
                                >
                                  {wo.findings || '-'}
                                </div>
                              </td>
                              <td>
                                {wo.flagged ? (
                                  <span
                                    className='badge bg-danger text-white d-inline-flex align-items-center gap-1'
                                    title={wo.flaggedComment || 'Flagged'}
                                  >
                                    <FlagIcon className='text-white' />
                                    Flagged
                                  </span>
                                ) : (
                                  <span className='text-muted'>-</span>
                                )}
                              </td>
                              <td>
                                <div
                                  className='text-truncate'
                                  style={{maxWidth: '180px'}}
                                  title={wo.flaggedComment || ''}
                                >
                                  {wo.flaggedComment || '-'}
                                </div>
                              </td>
                              <td>
                                <button
                                  type='button'
                                  className='btn btn-sm btn-light-primary'
                                  onClick={() => {
                                    setWorkOrderView(wo)
                                    setWorkOrderViewOpen(true)
                                  }}
                                >
                                  View Work Order
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {workOrders.length > 0 && (
                    <div className='d-flex align-items-center justify-content-between px-3 py-2 border-top'>
                      <div className='text-muted fs-8'>
                        Showing {workOrdersStart + 1}-
                        {Math.min(workOrdersStart + workOrdersPageSize, totalWorkOrders)} of{' '}
                        {totalWorkOrders}
                      </div>
                      <div className='d-flex align-items-center gap-2'>
                        <SearchableSelect
                          className='form-select form-select-sm text-dark'
                          style={{width: '90px'}}
                          value={workOrdersPageSize}
                          onChange={(e) => {
                            setWorkOrdersPageSize(Number(e.target.value))
                            setWorkOrdersPage(1)
                          }}
                        >
                          <option value={5}>5</option>
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                        </SearchableSelect>
                        <button
                          type='button'
                          className='btn btn-sm btn-light'
                          disabled={workOrdersPageSafe <= 1}
                          onClick={() => setWorkOrdersPage((prev) => Math.max(1, prev - 1))}
                        >
                          Prev
                        </button>
                        <div className='text-muted fs-8'>
                          Page {workOrdersPageSafe} of {workOrdersTotalPages}
                        </div>
                        <button
                          type='button'
                          className='btn btn-sm btn-light'
                          disabled={workOrdersPageSafe >= workOrdersTotalPages}
                          onClick={() =>
                            setWorkOrdersPage((prev) => Math.min(workOrdersTotalPages, prev + 1))
                          }
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Attachments */}
            <div className='mb-5'>
              <div className='d-flex justify-content-between align-items-center mb-3'>
                <h6 className='fw-bold m-0'>Attachments ({attachments.length})</h6>
                <button
                  type='button'
                  className='btn btn-sm btn-primary'
                  onClick={() => setAttachmentsModalOpen(true)}
                >
                  <KTSVG path='/media/icons/duotune/files/fil003.svg' className='svg-icon-2' />
                  Manage Attachments
                </button>
              </div>
              {loadingAttachments ? (
                <div className='text-center py-3'>
                  <div className='spinner-border spinner-border-sm' role='status'>
                    <span className='visually-hidden'>Loading...</span>
                  </div>
                </div>
              ) : attachments.length === 0 ? (
                <div className='text-muted text-center py-3'>No attachments</div>
              ) : (
                <div className='d-flex flex-wrap gap-2'>
                  {attachments.slice(0, 5).map((att) => (
                    <div key={att.id} className='badge badge-light-primary p-2'>
                      {att.fileName}
                    </div>
                  ))}
                  {attachments.length > 5 && (
                    <div className='badge badge-light p-2'>+{attachments.length - 5} more</div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className='border-top d-flex justify-content-end gap-2 px-4 py-3 flex-shrink-0 m-6'>
            <button type='button' className='btn btn-light btn-sm' onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Attachments Modal */}
      {attachmentsModalOpen && record.id && (
        <JobAttachmentsModal
          visible={true}
          jobId={record.id}
          onClose={() => {
            setAttachmentsModalOpen(false)
            loadAttachments()
            onRefresh()
          }}
          onSuccess={() => {
            loadAttachments()
            onRefresh()
          }}
        />
      )}

      {workOrderViewOpen && workOrderView && (
        <WorkOrderDetailsModal
          visible={workOrderViewOpen}
          workOrder={workOrderView}
          onClose={() => {
            setWorkOrderViewOpen(false)
            setWorkOrderView(null)
          }}
        />
      )}
      {record.planLineId && (
        <PlanLineLastDoneModal
          visible={lastDoneModalOpen}
          onClose={() => setLastDoneModalOpen(false)}
          job={record}
          onSaved={handleLastDoneSaved}
        />
      )}
    </>
  )
}
